import React from 'react'
import { createTableColumn } from '@fluentui/react-components'
import { NavLink } from 'react-router-dom'
import { Text } from '../components/common/texts/Text'
import { DemandComplianceModuleDefinition } from '../api/schemas/schema'
import { t } from '../i18n/i18n'
import { compareByDate, formatDate } from './dateHelper'
import { UseFormReset } from 'react-hook-form'
import { AnswerOption, AnswerOptionConnection } from '../api/schemas/schema'
import { IDataContext } from '../interfaces/IDataContext'
import { AddItemToCollection, RemoveItemFromCollection, UpdateItemInCollection, setLoading } from './stateHelper'
import { NavigateFunction } from 'react-router-dom'
import { api } from './apiHelper'

export const getDemandComplianceModuleColumns = () => [
   createTableColumn<DemandComplianceModuleDefinition>({
      columnId: 'order',
      compare: (a, b) => {
         return a.order - b.order
      },
      renderHeaderCell: () => {
         return <Text as="strong">{t('Order')}</Text>
      },
      renderCell: (item) => {
         return <Text as="strong">{item.order}</Text>
      },
   }),
   createTableColumn<DemandComplianceModuleDefinition>({
      columnId: 'name',
      compare: (a, b) => {
         return a.name.localeCompare(b.name)
      },
      renderHeaderCell: () => {
         return <Text as="strong">{t('Title')}</Text>
      },
      renderCell: (item) => {
         return (
            <NavLink style={{ textDecoration: 'none', color: 'black' }} to={`/system-settings/demand-compliance-modules/${item.id}`}>
               {item.name}
            </NavLink>
         )
      },
   }),
   createTableColumn<DemandComplianceModuleDefinition>({
      columnId: 'infoLabel',
      compare: (a, b) => {
         if (a.infoLabel === null && b.infoLabel === null) {
            return 0
         } else if (a.infoLabel === null) {
            return -1
         } else if (b.infoLabel === null) {
            return 1
         } else {
            return a.infoLabel.localeCompare(b.infoLabel)
         }
      },
      renderHeaderCell: () => {
         return <Text as="strong">{t('Info')}</Text>
      },
      renderCell: (item) => {
         return <Text>{item.infoLabel?.substring(0, 100)}</Text>
      },
   }),
   createTableColumn<DemandComplianceModuleDefinition>({
      columnId: 'active',
      compare: (a, b) => {
         return a.active.toString().localeCompare(b.active.toString())
      },
      renderHeaderCell: () => {
         return <Text as="strong">{t('Active')}</Text>
      },
      renderCell: (item) => {
         return <Text>{item.active ? t('Yes') : t('No')}</Text>
      },
   }),

   createTableColumn<DemandComplianceModuleDefinition>({
      columnId: 'modified',
      compare: (a, b) => {
         return compareByDate(a.modified ?? new Date(), b.modified ?? new Date())
      },
      renderHeaderCell: () => {
         return <Text as="strong">{t('Modified')}</Text>
      },
      renderCell: (item) => {
         return item.modified ? <Text>{formatDate(item.modified)}</Text> : null
      },
   }),
]

export const answerOptionConnections = (questionId: string, answerOptions: AnswerOption[]): AnswerOptionConnection[] => {
   const answerOptionConnections: AnswerOptionConnection[] = []
   for (let i = 0; i < answerOptions.length; i++) {
      answerOptionConnections.push({ answerOptionId: answerOptions[i].id, answerOption: answerOptions[i], questionId: questionId, demandId: null })
   }
   return answerOptionConnections
}

export const answerOptionConnection = (questionId: string, answerOptions: AnswerOption): AnswerOptionConnection => {
   const answerOptionConnection: AnswerOptionConnection = {
      answerOptionId: answerOptions.id,
      answerOption: answerOptions,
      questionId: questionId,
      demandId: null,
   }
   return answerOptionConnection
}

// export const choosenAnswerOptions = (answerForm: string, setValue: UseFormSetValue<QuestionFormValues>, answerOptions: AnswerOption[], questionId: string) => {
//    const selectedAnswerType = Number(answerForm)
//    if (selectedAnswerType === AnswerType.OwnAnswers) {
//       setValue('selectedQuestion.answerType', selectedAnswerType, { shouldDirty: true })
//       setValue('selectedQuestion.answerOptions', [])
//    } else {
//       const answerOptionConnection = answerOptionConnections(
//          questionId,
//          answerOptions.filter((x) => x.answerType === selectedAnswerType)
//       )
//       setValue('selectedQuestion.answerType', selectedAnswerType, { shouldDirty: true })
//       setValue('selectedQuestion.answerOptions', answerOptionConnection, { shouldDirty: true })
//    }
// }

export const createDemandComplianceModule = async (
   item: DemandComplianceModuleDefinition,
   dataContext: IDataContext,
   reset: UseFormReset<DemandComplianceModuleDefinition>,
   navigate: NavigateFunction
) => {
   try {
      const { accessToken } = dataContext.state
      const demandModule: DemandComplianceModuleDefinition = (await api(accessToken).demandModuleCreate(item)).data

      navigate(`/system-settings/demand-compliance-module-definitions/${demandModule.id}`)
      reset(demandModule)
      AddItemToCollection(dataContext.setRootState, dataContext.state.demandComplianceModuleDefinitions, demandModule, 'demandComplianceModuleDefinitions')
      dataContext.handleMessage(dataContext.setRootState, 'success', t('SavedSuccess'), '')
   } catch (error: any) {
      dataContext.handleMessage(dataContext.setRootState, 'error', t('SavedError'), error.message)
   } finally {
      setLoading(dataContext.setRootState, false)
   }
}

export const updateDemandComplianceModule = async (
   item: DemandComplianceModuleDefinition,
   dataContext: IDataContext,
   reset: UseFormReset<DemandComplianceModuleDefinition>
) => {
   try {
      const { accessToken } = dataContext.state
      const resp: any = await api(accessToken).demandModuleUpdate(item)
      dataContext.handleMessage(dataContext.setRootState, 'success', t('SavedSuccess'), '')
      UpdateItemInCollection(dataContext.setRootState, dataContext.state.demandComplianceModuleDefinitions, resp.data, 'demandComplianceModuleDefinitions')
      reset(resp.data)
   } catch (error: any) {
      dataContext.handleMessage(dataContext.setRootState, 'error', t('SavedError'), error.message)
   } finally {
      setLoading(dataContext.setRootState, false)
   }
}
export const deleteDemandComplianceModule = async (dataContext: IDataContext, item: DemandComplianceModuleDefinition, navigate: NavigateFunction) => {
   try {
      const { accessToken } = dataContext.state
      await api(accessToken).demandModuleDelete(item.id)
      dataContext.handleMessage(dataContext.setRootState, 'success', t('ItemDeleted'), '')
      RemoveItemFromCollection(dataContext.setRootState, dataContext.state.demandComplianceModuleDefinitions, item, 'demandComplianceModuleDefinitions')
      navigate('/system-settings')
   } catch (error: any) {
      dataContext.handleMessage(dataContext.setRootState, 'error', t('DeleteError'), error.message)
   } finally {
      setLoading(dataContext.setRootState, false)
   }
}

export const getNewAnswerOption = (): AnswerOption => {
   return { answerType: 2 }
}
